import React, { useMemo } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Space, Button, Alert, message } from "@mimber/ui-web"
import { Form, InputField, SelectField } from "@mimber/forms"
import {
  useCreateInvitation,
  useMembershipTiers,
  useRoles,
} from "@mimber/client/mimber-organization"
import type { SendInvitationFormProps } from "./SendInvitationForm.types"
import {
  sendInvitationSchema,
  sendOrgAdminInvitationSchema,
  type SendInvitationData,
} from "./SendInvitationForm.schema"

export const SendInvitationForm: React.FunctionComponent<
  SendInvitationFormProps
> = ({ onSuccess, isOrgAdminInvite = false }) => {
  const form = useForm<SendInvitationData>({
    resolver: zodResolver(
      isOrgAdminInvite ? sendOrgAdminInvitationSchema : sendInvitationSchema
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ) as any,
    mode: "onBlur",
    defaultValues: {
      name: "",
      email: "",
      tierId: undefined,
      organizationRoleId: undefined,
    },
  })

  const { reset } = form

  // Fetch membership tiers for tier selector
  const { data: tiersResponse, isLoading: tiersLoading } = useMembershipTiers({
    limit: 100, // Get all active tiers
  })

  const tierOptions = useMemo(() => {
    if (!tiersResponse?.data) return []
    return tiersResponse.data
      .filter((tier) => tier.isActive)
      .map((tier) => ({
        label: tier.name,
        value: tier.uuid,
      }))
  }, [tiersResponse])

  // Fetch organization roles for the role selector (org-admin mode only) —
  // only fires when a role can actually be assigned, avoiding a spurious
  // ROLE:READ-gated request/403 for users who only have INVITATION:CREATE
  const { data: rolesResponse, isLoading: rolesLoading } = useRoles({
    enabled: isOrgAdminInvite,
  })

  const roleOptions = useMemo(() => {
    if (!rolesResponse?.data) return []
    return rolesResponse.data.map((role) => ({
      label: role.name,
      value: role.uuid,
    }))
  }, [rolesResponse])

  const mutation = useCreateInvitation({
    onSuccess: (response) => {
      message.success("Invitation sent successfully")
      reset() // Reset form to default values
      onSuccess?.(response.data)
    },
    onError: (error) => {
      message.error(`Failed to send invitation: ${error.message}`)
    },
  })

  const onSubmit = (data: SendInvitationData) => {
    mutation.mutate(data)
  }

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      disabled={mutation.isPending}
      layout="vertical"
    >
      <Alert
        type="info"
        showIcon
        message="About invitations"
        description="The person will receive an email with a link to create their own Mimber account. They won't become a member of your organization until they accept."
        style={{ marginBottom: 16 }}
      />

      <InputField
        name="name"
        label="Name"
        placeholder="Enter invitee's full name"
        required
      />

      <InputField
        name="email"
        label="Email Address"
        type="email"
        placeholder="Enter invitee's email address"
        required
      />

      <SelectField
        name="tierId"
        label="Membership Tier"
        placeholder="Select a tier (optional)"
        options={tierOptions}
        loading={tiersLoading}
        allowClear
        showSearch
        filterOption={(input, option) =>
          String(option?.label ?? "")
            .toLowerCase()
            .includes(input.toLowerCase())
        }
      />

      {isOrgAdminInvite && (
        <SelectField
          name="organizationRoleId"
          label="Organization Role"
          placeholder="Select a role"
          options={roleOptions}
          loading={rolesLoading}
          showSearch
          required
          help='The role must include at least the "View Organization" permission for the invitee to log into the admin portal.'
          filterOption={(input, option) =>
            String(option?.label ?? "")
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        />
      )}

      <Space
        style={{ width: "100%", justifyContent: "flex-end", marginTop: 24 }}
      >
        <Button type="primary" htmlType="submit" loading={mutation.isPending}>
          Send Invitation
        </Button>
      </Space>
    </Form>
  )
}
